import React, { createContext, useContext, useEffect, useState } from 'react';
import { signInWithRedirect, signOut, getCurrentUser } from '@aws-amplify/auth';
import '../config/amplify';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    initializeAuth();
    
    // Listen for storage changes (when callback sets user data)
    const handleStorageChange = () => {
      initializeAuth();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const initializeAuth = async () => {
    try {
      // Check for existing user in session storage
      const storedUser = sessionStorage.getItem('userInfo');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setUserRole(userData.role);
        
        // Ensure admin users have proper role set
        if (userData.role === 'Carson_AdminUser') {
          console.log('Admin user detected, full menu access granted');
        }
      } else {
        setError('Authentication required');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };



  const login = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is already authenticated
      try {
        await getCurrentUser();
        // If we get here, user is already signed in, sign them out first
        await signOut({ global: true });
        setUser(null);
        setUserRole(null);
        sessionStorage.clear();
      } catch (error) {
        // User is not signed in, which is what we want
      }
      
      await signInWithRedirect();
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const signOutUser = async () => {
    try {
      await signOut();
      setUser(null);
      setUserRole(null);
      setError(null);
      sessionStorage.clear();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const value = {
    user,
    userRole,
    loading,
    error,
    login,
    signOut: signOutUser,
    logout: signOutUser,
    isAuthenticated: !!user,
    isAdmin: userRole === 'Carson_AdminUser',
    isTechUser: userRole === 'Carson_TechUser'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};